import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  getDoc,
  documentId,
  DocumentData,
  QueryDocumentSnapshot,
  writeBatch,
  limit,
  startAfter,
} from 'firebase/firestore'
import { db } from '../firebase'
// Types
export interface RecurrencePattern {
  frequency: 'weekly'
  days: number[] // 0=Sun, 1=Mon, ..., 6=Sat
  endDate: string // ISO date string (YYYY-MM-DD)
}

export interface Task {
  id?: string
  title: string
  studentId: string
  studentName?: string
  dueDate: string | null
  startDate?: string | null
  notes: string
  status: 'pending' | 'in-progress' | 'completed'
  reference?: string
  lessonId?: string
  createdAt?: string | null
  updatedAt?: string | null
  completedDate?: string | null // Added: timestamp when task was marked completed
  // Recurring task fields (backward-compatible — all optional, default null/false)
  recurringGroupId?: string | null // UUID linking all occurrences in a series
  recurrencePattern?: RecurrencePattern | null // Pattern config stored on each occurrence
  isRecurring?: boolean // Quick filter flag
}
// Global cache to store tasks
// This will persist across component unmounts/remounts during navigation
export const tasksCache = {
  data: null,
  timestamp: 0,
  userId: null,
}
// Task cache validity duration - 5 minutes
export const CACHE_VALIDITY_DURATION = 5 * 60 * 1000
// Cache to store student name lookup results
const studentNameCache: Record<string, { name: string; timestamp: number }> = {}
const STUDENT_CACHE_VALIDITY = 30 * 60 * 1000 // 30 minutes
// P0 SCALABILITY FIX: Batch size for student lookups
const BATCH_SIZE = 10 // Firestore 'in' query limit
// SPAM-CLICK PROTECTION: Lock flag to prevent concurrent task creation
let isCreatingTask = false

/**
 * Resolve the linkedUid for a given (teacherId, studentId) pair.
 *
 * Reads the roster doc at teachers/{teacherId}/students/{studentId} and
 * returns its `linkedUid` field. Returns null if the doc is missing,
 * unreadable, or the student is not yet linked to a Firebase Auth user.
 *
 * This value is denormalized onto every task at write/update time so the
 * Student Dashboard can issue a list query of the form
 *   where('linkedUid', '==', auth.uid)
 * which the Firestore rules query-safety analyzer can statically validate.
 */
const resolveLinkedUid = async (
  teacherId: string,
  studentId: string,
): Promise<string | null> => {
  if (!teacherId || !studentId) return null
  try {
    const rosterRef = doc(db, 'teachers', teacherId, 'students', studentId)
    const snap = await getDoc(rosterRef)
    if (!snap.exists()) return null
    const data = snap.data() as { linkedUid?: string | null } | undefined
    return data?.linkedUid && typeof data.linkedUid === 'string'
      ? data.linkedUid
      : null
  } catch (err) {
    console.warn('[taskService] resolveLinkedUid failed; defaulting to null', {
      teacherId,
      studentId,
      err,
    })
    return null
  }
}
/**
 * Get all tasks for a teacher - optimized version with batch student fetching
 * @param teacherId The ID of the teacher
 * @returns Array of task objects
 */
export const getTeacherTasks = async (teacherId: string): Promise<Task[]> => {
  if (!teacherId) {
    console.error('No teacher ID provided to getTeacherTasks')
    return []
  }

  try {
    console.log(
      '[P0 FIX] Starting optimized task fetch with batch student lookup for teacher:',
      teacherId,
    )

    // Step 1: Fetch all tasks
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const tasksQuery = query(tasksRef, orderBy('dueDate', 'asc'))
    const tasksSnapshot = await getDocs(tasksQuery)

    console.log(
      `[P0 FIX] Found ${tasksSnapshot.docs.length} tasks in main collection`,
    )

    if (tasksSnapshot.empty) {
      return []
    }

    // Step 2: Extract unique student IDs
    const uniqueStudentIds = new Set<string>()
    const taskDocs = tasksSnapshot.docs.map((doc) => {
      const data = doc.data()
      if (data.studentId) {
        uniqueStudentIds.add(data.studentId)
      }
      return { id: doc.id, data }
    })

    console.log(
      `[P0 FIX] Found ${uniqueStudentIds.size} unique students across ${taskDocs.length} tasks`,
    )

    // Step 3: Batch-fetch student names (Firestore 'in' query limit is 10)
    const studentIdArray = Array.from(uniqueStudentIds)
    const studentNameMap = new Map<string, string>()

    // Process in batches of 10 (Firestore limit for 'in' queries)
    for (let i = 0; i < studentIdArray.length; i += BATCH_SIZE) {
      const batch = studentIdArray.slice(i, i + BATCH_SIZE)

      try {
        const studentsRef = collection(db, 'teachers', teacherId, 'students')
        const studentsQuery = query(
          studentsRef,
          where(documentId(), 'in', batch),
        )
        const studentsSnapshot = await getDocs(studentsQuery)

        studentsSnapshot.forEach((studentDoc) => {
          const studentData = studentDoc.data()
          studentNameMap.set(
            studentDoc.id,
            studentData.name || 'Unknown Student',
          )
        })

        console.log(
          `[P0 FIX] Batch ${Math.floor(i / BATCH_SIZE) + 1}: Fetched ${studentsSnapshot.size} student names`,
        )
      } catch (err) {
        console.error(
          `[P0 FIX] Error fetching student batch ${Math.floor(i / BATCH_SIZE) + 1}:`,
          err,
        )
      }
    }

    console.log(`[P0 FIX] Total student names fetched: ${studentNameMap.size}`)

    // Step 4: Map tasks with student names
    const tasks: Task[] = taskDocs.map(({ id, data }) => {
      const studentId = data.studentId || ''
      const studentName =
        studentNameMap.get(studentId) || data.studentName || 'Unknown Student'

      return {
        id,
        title: data.title || '',
        studentId,
        studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
        // Recurring task fields (backward-compatible defaults)
        recurringGroupId: data.recurringGroupId || null,
        recurrencePattern: data.recurrencePattern || null,
        isRecurring: data.isRecurring || false,
      } as Task
    })

    // Sort by due date
    tasks.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate) : new Date(0)
      const dateB = b.dueDate ? new Date(b.dueDate) : new Date(0)
      return dateA.getTime() - dateB.getTime()
    })

    console.log(
      `[P0 FIX] Processed ${tasks.length} tasks with batched student names`,
    )
    console.log(
      `[P0 FIX] Firestore reads: 1 (tasks) + ${Math.ceil(uniqueStudentIds.size / BATCH_SIZE)} (students in batches) = ${1 + Math.ceil(uniqueStudentIds.size / BATCH_SIZE)} total`,
    )

    return tasks
  } catch (error) {
    console.error('[P0 FIX] Error fetching teacher tasks:', error)
    return []
  }
}

/**
 * Get paginated tasks for a teacher
 * @param teacherId The ID of the teacher
 * @param lastDoc The last document from the previous query (for pagination)
 * @param pageSize The number of tasks to fetch (default 25)
 * @returns Object containing tasks and the last document snapshot
 */
export const getTeacherTasksPaginated = async (
  teacherId: string,
  lastDocSnap?: QueryDocumentSnapshot<DocumentData> | null,
  pageSize: number = 25,
): Promise<{
  tasks: Task[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}> => {
  if (!teacherId) {
    console.error('No teacher ID provided to getTeacherTasksPaginated')
    return { tasks: [], lastDoc: null, hasMore: false }
  }

  try {
    console.log(
      '[PAGINATION] Starting paginated task fetch for teacher:',
      teacherId,
    )

    // Step 1: Fetch tasks with pagination
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')

    let tasksQuery
    if (lastDocSnap) {
      tasksQuery = query(
        tasksRef,
        orderBy('dueDate', 'asc'),
        startAfter(lastDocSnap),
        limit(pageSize),
      )
    } else {
      tasksQuery = query(tasksRef, orderBy('dueDate', 'asc'), limit(pageSize))
    }

    const tasksSnapshot = await getDocs(tasksQuery)

    console.log(
      `[PAGINATION] Found ${tasksSnapshot.docs.length} tasks in this page`,
    )

    if (tasksSnapshot.empty) {
      return { tasks: [], lastDoc: null, hasMore: false }
    }

    const newLastDoc = tasksSnapshot.docs[tasksSnapshot.docs.length - 1]
    const hasMore = tasksSnapshot.docs.length === pageSize

    // Step 2: Extract unique student IDs
    const uniqueStudentIds = new Set<string>()
    const taskDocs = tasksSnapshot.docs.map((doc) => {
      const data = doc.data()
      if (data.studentId) {
        uniqueStudentIds.add(data.studentId)
      }
      return { id: doc.id, data }
    })

    // Step 3: Batch-fetch student names (Firestore 'in' query limit is 10)
    const studentIdArray = Array.from(uniqueStudentIds)
    const studentNameMap = new Map<string, string>()

    // Process in batches of 10 (Firestore limit for 'in' queries)
    for (let i = 0; i < studentIdArray.length; i += BATCH_SIZE) {
      const batch = studentIdArray.slice(i, i + BATCH_SIZE)

      try {
        const studentsRef = collection(db, 'teachers', teacherId, 'students')
        const studentsQuery = query(
          studentsRef,
          where(documentId(), 'in', batch),
        )
        const studentsSnapshot = await getDocs(studentsQuery)

        studentsSnapshot.forEach((studentDoc) => {
          const studentData = studentDoc.data()
          studentNameMap.set(
            studentDoc.id,
            studentData.name || 'Unknown Student',
          )
        })
      } catch (err) {
        console.error(
          `[PAGINATION] Error fetching student batch ${Math.floor(i / BATCH_SIZE) + 1}:`,
          err,
        )
      }
    }

    // Step 4: Map tasks with student names
    const tasks: Task[] = taskDocs.map(({ id, data }) => {
      const studentId = data.studentId || ''
      const studentName =
        studentNameMap.get(studentId) || data.studentName || 'Unknown Student'

      return {
        id,
        title: data.title || '',
        studentId,
        studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
        // Recurring task fields (backward-compatible defaults)
        recurringGroupId: data.recurringGroupId || null,
        recurrencePattern: data.recurrencePattern || null,
        isRecurring: data.isRecurring || false,
      } as Task
    })

    // Sort by due date (already sorted by query, but good to be safe)
    tasks.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate) : new Date(0)
      const dateB = b.dueDate ? new Date(b.dueDate) : new Date(0)
      return dateA.getTime() - dateB.getTime()
    })

    return { tasks, lastDoc: newLastDoc, hasMore }
  } catch (error) {
    console.error('[PAGINATION] Error fetching teacher tasks:', error)
    return { tasks: [], lastDoc: null, hasMore: false }
  }
}

/**
 * Get tasks for a specific student
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @returns Array of task objects for the student
 */
export const getStudentTasks = async (
  teacherId: string,
  studentId: string,
): Promise<Task[]> => {
  if (!teacherId || !studentId) {
    console.error('Missing teacher ID or student ID')
    return []
  }
  try {
    console.log(`Fetching tasks for student document ID: ${studentId}`)
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const tasksQuery = query(
      tasksRef,
      where('studentId', '==', studentId),
      orderBy('dueDate', 'asc'),
    )
    const tasksSnapshot = await getDocs(tasksQuery)
    let studentName = 'Unknown Student'
    try {
      const studentDoc = await getDoc(
        doc(db, 'teachers', teacherId, 'students', studentId),
      )
      if (studentDoc.exists()) {
        studentName = studentDoc.data().name || 'Unknown Student'
      }
    } catch (err) {
      console.error(`Error fetching student name for ${studentId}:`, err)
    }
    return tasksSnapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        title: data.title || '',
        studentId: studentId,
        studentName: studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
        // Recurring task fields (backward-compatible defaults)
        recurringGroupId: data.recurringGroupId || null,
        recurrencePattern: data.recurrencePattern || null,
        isRecurring: data.isRecurring || false,
      } as Task
    })
  } catch (error) {
    console.error('Error fetching student tasks:', error)
    return []
  }
}
/**
 * Add a new task for a student
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param task The task data to add
 * @returns The ID of the newly created task
 */
export const addTask = async (
  teacherId: string,
  studentId: string,
  task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> => {
  // SPAM-CLICK PROTECTION: Check if task creation is already in progress
  if (isCreatingTask) {
    throw new Error('Task creation already in progress. Please wait.')
  }

  if (!teacherId || !studentId) {
    throw new Error('Missing teacher ID or student ID')
  }

  // SPAM-CLICK PROTECTION: Set lock before starting
  isCreatingTask = true

  try {
    console.log(`Adding task for student document ID: ${studentId}`)

    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')

    let dueDate = task.dueDate
    if (typeof dueDate === 'string') {
      // Fix timezone issue: treat date as local date, not UTC
      const dateObj = new Date(dueDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        dueDate = Timestamp.fromDate(localDate)
      } else {
        dueDate = Timestamp.fromDate(dateObj)
      }
    }

    let startDate = null
    if (task.startDate && typeof task.startDate === 'string') {
      const dateObj = new Date(task.startDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (task.startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = task.startDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        startDate = Timestamp.fromDate(localDate)
      } else {
        startDate = Timestamp.fromDate(dateObj)
      }
    }

    // Resolve linkedUid from the student roster doc so the student-side
    // dashboard can safely query its own tasks via where('linkedUid','==',uid).
    const linkedUid = await resolveLinkedUid(teacherId, studentId)

    const taskData: any = {
      title: task.title,
      studentId: studentId,
      studentName: task.studentName || '',
      // Denormalized access claim — Firestore rules read this directly
      // instead of doing a get() into the roster doc.
      linkedUid: linkedUid,
      dueDate: dueDate,
      startDate: startDate,
      notes: task.notes || '',
      status: task.status || 'pending',
      reference: task.reference || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Recurring task fields (backward-compatible defaults)
      recurringGroupId: task.recurringGroupId || null,
      recurrencePattern: task.recurrencePattern || null,
      isRecurring: task.isRecurring || false,
    }

    // Add lessonId if provided
    if (task.lessonId) {
      taskData.lessonId = task.lessonId
    }

    const docRef = await addDoc(tasksRef, taskData)
    console.log('Task added with ID:', docRef.id)

    if (task.studentName) {
      studentNameCache[studentId] = {
        name: task.studentName,
        timestamp: Date.now(),
      }
    }

    return docRef.id
  } catch (error) {
    console.error('Error adding task:', error)
    throw error
  } finally {
    // SPAM-CLICK PROTECTION: Always release lock, even on error
    isCreatingTask = false
  }
}
/**
 * Update an existing task
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param taskId The ID of the task to update
 * @param task The updated task data
 * @returns A boolean indicating success
 */
export const updateTask = async (
  teacherId: string,
  studentId: string,
  taskId: string,
  task: Partial<Task>,
): Promise<boolean> => {
  if (!teacherId || !studentId || !taskId) {
    throw new Error('Missing teacher ID, student ID, or task ID')
  }

  try {
    console.log(`Updating task ${taskId} for student document ID: ${studentId}`)

    const taskRef = doc(db, 'teachers', teacherId, 'tasks', taskId)

    let dueDate = task.dueDate
    if (dueDate && typeof dueDate === 'string') {
      // Fix timezone issue: treat date as local date, not UTC
      const dateObj = new Date(dueDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        dueDate = Timestamp.fromDate(localDate)
      } else {
        dueDate = Timestamp.fromDate(dateObj)
      }
    }

    let startDate = task.startDate
    if (startDate && typeof startDate === 'string') {
      const dateObj = new Date(startDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = startDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        startDate = Timestamp.fromDate(localDate)
      } else {
        startDate = Timestamp.fromDate(dateObj)
      }
    }

    const updateData: any = {
      updatedAt: serverTimestamp(),
    }

    if (task.title !== undefined) updateData.title = task.title
    if (dueDate !== undefined && dueDate !== null) updateData.dueDate = dueDate
    if (startDate !== undefined && startDate !== null)
      updateData.startDate = startDate
    if (task.notes !== undefined) updateData.notes = task.notes
    if (task.status !== undefined) {
      updateData.status = task.status
      // Add completedDate when task is marked as completed
      if (task.status === 'completed') {
        updateData.completedDate = serverTimestamp()
      }
    }
    if (task.reference !== undefined) updateData.reference = task.reference

    // Handle lessonId updates
    if (task.lessonId !== undefined) {
      updateData.lessonId = task.lessonId
    }

    // Opportunistic per-task linkedUid refresh.
    // If the student has been linked since this task was created (or
    // re-linked to a different uid), keep the task's denormalized
    // linkedUid in sync. Skips on lookup failure to avoid blocking the
    // primary update.
    try {
      const freshLinkedUid = await resolveLinkedUid(teacherId, studentId)
      updateData.linkedUid = freshLinkedUid
    } catch {
      // non-fatal; leave linkedUid untouched
    }

    await updateDoc(taskRef, updateData)
    console.log('Task updated successfully:', taskId)

    return true
  } catch (error) {
    console.error('Error updating task:', error)
    throw error
  }
}
/**
 * Delete a task
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param taskId The ID of the task to delete
 * @returns A boolean indicating success
 */
export const deleteTask = async (
  teacherId: string,
  studentId: string,
  taskId: string,
): Promise<boolean> => {
  if (!teacherId || !studentId || !taskId) {
    throw new Error('Missing teacher ID, student ID, or task ID')
  }
  try {
    console.log(`Deleting task ${taskId} for student document ID: ${studentId}`)
    // Delete from main tasks collection
    const taskRef = doc(db, 'teachers', teacherId, 'tasks', taskId)
    await deleteDoc(taskRef)
    console.log('Task deleted successfully:', taskId)
    return true
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}

/**
 * Create multiple recurring task occurrences in a single batch write.
 * Generates due dates based on the recurrence pattern (weekly + selected days)
 * and creates one task document per occurrence.
 *
 * @param teacherId - The teacher's UID
 * @param studentId - The student document ID
 * @param baseTask - Base task data (title, notes, reference, studentName)
 * @param recurrence - Recurrence config: frequency, days (0=Sun..6=Sat), endDate (YYYY-MM-DD)
 * @returns Object with recurringGroupId and count of tasks created
 */
export const createRecurringTasks = async (
  teacherId: string,
  studentId: string,
  baseTask: {
    title: string
    notes?: string
    reference?: string
    studentName?: string
  },
  recurrence: RecurrencePattern,
): Promise<{ recurringGroupId: string; count: number }> => {
  if (!teacherId || !studentId) {
    throw new Error('Missing teacher ID or student ID')
  }
  if (!recurrence.days || recurrence.days.length === 0) {
    throw new Error('At least one day must be selected for recurring tasks')
  }
  if (!recurrence.endDate) {
    throw new Error('End date is required for recurring tasks')
  }

  const recurringGroupId = crypto.randomUUID()

  console.log('[RECURRING TASKS] Creating recurring tasks:', {
    teacherId,
    studentId,
    title: baseTask.title,
    recurrence,
    recurringGroupId,
  })

  // Calculate all due dates from today through endDate
  const dueDates: Date[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const endDate = new Date(recurrence.endDate + 'T23:59:59')

  // Walk day-by-day from today to endDate
  const cursor = new Date(today)
  while (cursor <= endDate) {
    const dayOfWeek = cursor.getDay() // 0=Sun, 1=Mon, ..., 6=Sat
    if (recurrence.days.includes(dayOfWeek)) {
      dueDates.push(new Date(cursor))
    }
    cursor.setDate(cursor.getDate() + 1)
  }

  console.log(
    '[RECURRING TASKS] Generated',
    dueDates.length,
    'due dates:',
    dueDates.map((d) => d.toISOString().split('T')[0]),
  )

  if (dueDates.length === 0) {
    throw new Error(
      'No matching dates found for the selected days and end date',
    )
  }

  // Create all tasks in a single batch (Firestore limit: 500 per batch — safe for recurring tasks)
  const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
  const batch = writeBatch(db)

  // Resolve linkedUid ONCE (all batched tasks share the same student)
  const linkedUid = await resolveLinkedUid(teacherId, studentId)

  dueDates.forEach((dueDate) => {
    const taskDocRef = doc(tasksRef) // auto-generated ID
    batch.set(taskDocRef, {
      title: baseTask.title,
      studentId: studentId,
      studentName: baseTask.studentName || '',
      // Denormalized access claim for the student-side list query.
      linkedUid: linkedUid,
      dueDate: Timestamp.fromDate(dueDate),
      startDate: null,
      notes: baseTask.notes || '',
      status: 'pending',
      reference: baseTask.reference || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Recurring fields
      recurringGroupId: recurringGroupId,
      recurrencePattern: recurrence,
      isRecurring: true,
    })
  })

  await batch.commit()

  console.log(
    '[RECURRING TASKS] ✅ Successfully created',
    dueDates.length,
    'recurring tasks with groupId:',
    recurringGroupId,
  )

  return { recurringGroupId, count: dueDates.length }
}

/**
 * Delete recurring tasks by recurringGroupId.
 * Uses a single `where` clause (no composite index needed) + client-side date filtering.
 * Same proven pattern as the calendar's handleRecurringDelete.
 *
 * @param teacherId - The teacher's UID
 * @param recurringGroupId - The shared UUID linking all tasks in the series
 * @param scope - 'all' deletes every task in the series; 'future' deletes only tasks with dueDate >= referenceDate
 * @param referenceDate - ISO date string (YYYY-MM-DD) used as the cutoff for 'future' scope
 * @returns Object with count of deleted tasks
 */
export const deleteRecurringTasks = async (
  teacherId: string,
  recurringGroupId: string,
  scope: 'all' | 'future',
  referenceDate?: string,
): Promise<{ count: number }> => {
  if (!teacherId || !recurringGroupId) {
    throw new Error('Missing teacher ID or recurringGroupId')
  }
  if (scope === 'future' && !referenceDate) {
    throw new Error('referenceDate is required for "future" scope')
  }

  console.log(
    '[RECURRING TASKS DELETE] Step 1: Querying tasks with recurringGroupId:',
    recurringGroupId,
  )

  // Single where clause — no composite index required
  const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
  const q = query(tasksRef, where('recurringGroupId', '==', recurringGroupId))
  const snapshot = await getDocs(q)

  console.log(
    '[RECURRING TASKS DELETE] Step 2: Found',
    snapshot.size,
    'tasks in series',
  )

  // Client-side filtering for 'future' scope
  let docsToDelete = snapshot.docs
  if (scope === 'future' && referenceDate) {
    const cutoff = new Date(referenceDate)
    cutoff.setHours(0, 0, 0, 0)

    docsToDelete = snapshot.docs.filter((docSnap) => {
      const data = docSnap.data()
      let taskDate: Date | null = null

      // Handle both Timestamp and string dueDate formats
      if (data.dueDate && typeof data.dueDate.toDate === 'function') {
        taskDate = data.dueDate.toDate()
      } else if (data.dueDate && typeof data.dueDate === 'string') {
        taskDate = new Date(data.dueDate)
      }

      if (!taskDate) return false
      return taskDate >= cutoff
    })

    console.log(
      '[RECURRING TASKS DELETE] Step 2b: After future-filter,',
      docsToDelete.length,
      'tasks to delete (cutoff:',
      referenceDate,
      ')',
    )
  }

  if (docsToDelete.length === 0) {
    console.log('[RECURRING TASKS DELETE] No tasks to delete')
    return { count: 0 }
  }

  // Batch delete (Firestore limit: 500 per batch — safe for recurring tasks)
  console.log(
    '[RECURRING TASKS DELETE] Step 3: Batch deleting',
    docsToDelete.length,
    'tasks',
  )
  const batch = writeBatch(db)
  docsToDelete.forEach((docSnap) => {
    batch.delete(docSnap.ref)
  })

  await batch.commit()

  console.log(
    '[RECURRING TASKS DELETE] Step 4: ✅ Successfully deleted',
    docsToDelete.length,
    'recurring tasks',
  )

  return { count: docsToDelete.length }
}

/**
 * Update recurring tasks by scope: single, future, or all.
 * Only updates the provided fields — never overwrites dueDate, createdAt, or recurringGroupId.
 */
export async function updateRecurringTasks(
  teacherId: string,
  recurringGroupId: string,
  scope: 'future' | 'all',
  referenceDate: string | undefined,
  updates: Record<string, unknown>,
): Promise<{ count: number }> {
  if (!teacherId || !recurringGroupId) {
    throw new Error('Missing teacher ID or recurringGroupId')
  }
  if (scope === 'future' && !referenceDate) {
    throw new Error('referenceDate is required for "future" scope')
  }

  // Never allow overwriting these fields in batch updates
  const safeUpdates = { ...updates }
  delete safeUpdates.dueDate
  delete safeUpdates.dueAt
  delete safeUpdates.createdAt
  delete safeUpdates.recurringGroupId
  delete safeUpdates.id

  console.log(
    '[RECURRING TASKS UPDATE] Step 1: Querying tasks with recurringGroupId:',
    recurringGroupId,
  )

  const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
  const q = query(tasksRef, where('recurringGroupId', '==', recurringGroupId))
  const snapshot = await getDocs(q)

  console.log(
    '[RECURRING TASKS UPDATE] Step 2: Found',
    snapshot.size,
    'tasks in series',
  )

  // Client-side filtering for 'future' scope
  let docsToUpdate = snapshot.docs
  if (scope === 'future' && referenceDate) {
    const cutoff = new Date(referenceDate)
    cutoff.setHours(0, 0, 0, 0)

    docsToUpdate = snapshot.docs.filter((docSnap) => {
      const data = docSnap.data()
      let taskDate: Date | null = null

      if (data.dueDate && typeof data.dueDate.toDate === 'function') {
        taskDate = data.dueDate.toDate()
      } else if (data.dueDate && typeof data.dueDate === 'string') {
        taskDate = new Date(data.dueDate)
      }

      if (!taskDate) return false
      return taskDate >= cutoff
    })

    console.log(
      '[RECURRING TASKS UPDATE] Step 2b: After future-filter,',
      docsToUpdate.length,
      'tasks to update (cutoff:',
      referenceDate,
      ')',
    )
  }

  if (docsToUpdate.length === 0) {
    console.log('[RECURRING TASKS UPDATE] No tasks to update')
    return { count: 0 }
  }

  // Batch update (Firestore limit: 500 per batch — safe for recurring tasks)
  console.log(
    '[RECURRING TASKS UPDATE] Step 3: Batch updating',
    docsToUpdate.length,
    'tasks with fields:',
    Object.keys(safeUpdates),
  )
  const batch = writeBatch(db)
  docsToUpdate.forEach((docSnap) => {
    batch.update(docSnap.ref, safeUpdates)
  })

  await batch.commit()

  console.log(
    '[RECURRING TASKS UPDATE] Step 4: ✅ Successfully updated',
    docsToUpdate.length,
    'recurring tasks',
  )

  return { count: docsToUpdate.length }
}

/**
 * Idempotent backfill: ensure every task under teachers/{teacherId}/tasks
 * carries a `linkedUid` field denormalized from its student roster doc.
 *
 * Safe to call repeatedly on every teacher dashboard mount. Tasks that
 * already have a `linkedUid` (string or null) are skipped, so the cost is
 * one collection list + one roster get per unique student that still
 * needs a value.
 *
 * The teacher's own credentials are used; the Firestore rule
 *   allow read, write: if request.auth.uid == teacherId
 * already grants this. No rule change required to run the backfill.
 *
 * Returns a small report. Errors are logged and swallowed so a backfill
 * failure never blocks the rest of the dashboard.
 */
export const backfillTeacherTaskLinkedUids = async (
  teacherId: string,
): Promise<{ scanned: number; updated: number; skipped: number }> => {
  if (!teacherId) return { scanned: 0, updated: 0, skipped: 0 }
  try {
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const snapshot = await getDocs(tasksRef)
    const scanned = snapshot.size

    if (snapshot.empty) {
      console.log(
        '[backfillTeacherTaskLinkedUids] no tasks found for teacher',
        teacherId,
      )
      return { scanned: 0, updated: 0, skipped: 0 }
    }

    // Per-student resolution cache so we only read each roster doc once.
    const linkedUidCache = new Map<string, string | null>()

    const toUpdate: {
      ref: ReturnType<typeof doc>
      linkedUid: string | null
    }[] = []
    let skipped = 0

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data() as {
        studentId?: string
        linkedUid?: string | null
      }
      // Self-heal nulls: skip ONLY when linkedUid is already a non-empty
      // string. Docs with `linkedUid: null` or a missing field are
      // re-resolved against the current roster doc. This matches the
      // calendar backfill (backfillTeacherCalendarLinkedUids) so tasks
      // that were stamped null when the student was unlinked recover
      // automatically once the student links their account.
      const existing = data.linkedUid
      if (typeof existing === 'string' && existing.length > 0) {
        skipped++
        continue
      }
      if (!data.studentId || typeof data.studentId !== 'string') {
        // Malformed task — can't resolve. Write null so future passes skip it.
        toUpdate.push({ ref: docSnap.ref, linkedUid: null })
        continue
      }

      let linkedUid: string | null
      if (linkedUidCache.has(data.studentId)) {
        linkedUid = linkedUidCache.get(data.studentId) as string | null
      } else {
        linkedUid = await resolveLinkedUid(teacherId, data.studentId)
        linkedUidCache.set(data.studentId, linkedUid)
      }
      toUpdate.push({ ref: docSnap.ref, linkedUid })
    }

    if (toUpdate.length === 0) {
      console.log('[backfillTeacherTaskLinkedUids] nothing to backfill', {
        teacherId,
        scanned,
        skipped,
      })
      return { scanned, updated: 0, skipped }
    }

    // Batch writes (Firestore limit is 500; cap at 450 for headroom).
    const BATCH_LIMIT = 450
    let updated = 0
    for (let i = 0; i < toUpdate.length; i += BATCH_LIMIT) {
      const slice = toUpdate.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      slice.forEach(({ ref, linkedUid }) => {
        batch.update(ref, { linkedUid })
      })
      await batch.commit()
      updated += slice.length
    }

    console.log('[backfillTeacherTaskLinkedUids] done', {
      teacherId,
      scanned,
      updated,
      skipped,
    })
    return { scanned, updated, skipped }
  } catch (err) {
    console.warn(
      '[backfillTeacherTaskLinkedUids] failed; non-fatal, will retry next mount',
      err,
    )
    return { scanned: 0, updated: 0, skipped: 0 }
  }
}
